import React, { useState, useEffect, useCallback } from 'react';
import PriceChangeSinceReport from './PriceChangeSinceReport';
import { ExternalLink, ChevronLeft, ChevronRight, FileText, BookOpen, Lock } from 'lucide-react';
import { apiClient } from '../config/api';
import { cleanTitle } from '../utils/reportTitle';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { hasFullAccess } = useAuth();
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 25;

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort: 'desc',
      });

      if (activeTab === 'research') params.append('type', 'research');
      if (searchTerm) params.append(activeTab === 'research' ? 'activist' : 'company', searchTerm);

      const isVercel = window.location.hostname.includes('vercel.app');
      let data, total;

      if (isVercel) {
        const response = await fetch(`/api/short-sellers?${params}`);
        total = response.headers.get('X-Total-Count') || 0;
        data = await response.json();
      } else {
        const response = await apiClient.get(`/api/short-sellers?${params}`);
        total = response.headers?.get?.('X-Total-Count') || response.headers?.['x-total-count'] || 0;
        data = response.data;
      }

      setReports(Array.isArray(data) ? data : []);
      setTotalCount(parseInt(total) || data?.length || 0);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, activeTab]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setSearchTerm('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalCount / limit);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={fetchReports} className="text-sm text-gray-900 border-b border-gray-900 hover:text-gray-600">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-light text-gray-900">Short Thesis Reports</h1>
          <p className="text-sm text-gray-500 mt-1">{totalCount.toLocaleString()} {activeTab === 'research' ? 'research pieces' : 'reports'}</p>
        </div>
        {hasFullAccess && (
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              placeholder={activeTab === 'research' ? 'Search publisher...' : 'Search company...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm w-48 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
            />
            <button type="submit" className="px-4 py-2 bg-gray-900 text-white text-sm rounded hover:bg-gray-800 transition-colors">
              Search
            </button>
          </form>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-0">
        <button
          onClick={() => handleTabChange('reports')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'reports'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          Company Reports
        </button>
        <button
          onClick={() => handleTabChange('research')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'research'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          Research &amp; Commentary
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 border-t-0 rounded-b overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                {activeTab === 'reports' ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">Title</th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">Title</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activist</th>
                {activeTab === 'reports' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sector</th>
                )}
                {activeTab === 'reports' && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      title="Change from the close before the report to the latest close">
                    Since report
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-500">Loading...</td></tr>
              ) : reports.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-500">No reports found</td></tr>
              ) : reports.map((report, index) => (
                <tr key={report.id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(report.report_date)}</td>
                  {activeTab === 'reports' ? (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-900">{report.company_name || '-'}</td>
                      <td className="px-4 py-3"><span className="text-sm font-mono text-gray-600">{report.company_ticker || '-'}</span></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{cleanTitle(report.report_title) || '-'}</td>
                    </>
                  ) : (
                    <td className="px-4 py-3 text-sm text-gray-900">{cleanTitle(report.report_title) || '-'}</td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{report.activist_short_seller || '-'}</td>
                  {activeTab === 'reports' && (
                    <td className="px-4 py-3 text-sm text-gray-500">{report.sector || '-'}</td>
                  )}
                  {activeTab === 'reports' && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <PriceChangeSinceReport
                        pct={report.pct_change_since_report}
                        preClose={report.pre_report_close}
                        latestClose={report.latest_close}
                        asOf={report.latest_close_date}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm">
                    {report.report_url ? (
                      <a href={report.report_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900">
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Full-access users get pagination; limited viewers see only the
            first page, with an upgrade prompt in place of the pager. */}
        {hasFullAccess ? (
          totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                {((page - 1) * limit) + 1}–{Math.min(page * limit, totalCount)} of {totalCount.toLocaleString()}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 min-w-[80px] text-center">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          )
        ) : (
          totalCount > limit && (
            <div className="px-4 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Lock className="w-4 h-4 text-gray-400" />
                <span>
                  Showing the latest <strong>{Math.min(limit, totalCount)}</strong> of{' '}
                  <strong>{totalCount.toLocaleString()}</strong> — upgrade for full access.
                </span>
              </div>
              <button
                type="button"
                title="Coming soon"
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 transition-colors"
              >
                Get full access
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Dashboard;
